/**
 * Lightweight Auth GraphQL Client
 * 
 * Minimal GraphQL client for authentication operations only.
 * This client is used by AuthProvider before URQL is initialized with auth headers.
 * 
 * Design decisions:
 * - Separate from URQL to avoid circular dependency (need auth to init URQL)
 * - Simple fetch-based implementation (no caching, no circuit breaker for auth)
 * - Includes CSRF protection headers
 * - Used only for: login, refresh token, me query, logout
 * 
 * After authentication, all other GraphQL operations use URQL client.
 */

import { config } from "../config";
import { getInitData } from "./telegram";
import { getStoredTokens, isTokenExpired } from "./storage";
import { createLogger } from "./logger";

const authLog = createLogger("AuthClient");

const GRAPHQL_URL = `${config.apiUrl}/graphql`;
const REQUEST_TIMEOUT = 30000;

// =============================================================================
// TYPES
// =============================================================================

interface GraphQLResponse<T> {
  data?: T;
  errors?: Array<{ message: string; extensions?: { code?: string } }>;
}

/**
 * Custom error class for auth GraphQL errors
 */
export class AuthGraphQLError extends Error {
  public readonly code: string;
  public readonly isNetworkError: boolean;

  constructor(
    message: string,
    options: { code?: string; isNetworkError?: boolean } = {}
  ) {
    super(message);
    this.name = "AuthGraphQLError";
    this.code = options.code ?? "UNKNOWN";
    this.isNetworkError = options.isNetworkError ?? false;
  }
}

// =============================================================================
// HEADERS
// =============================================================================

/**
 * Get auth headers for requests.
 * Priority: Telegram initData > JWT Bearer token
 */
function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    // CSRF defense-in-depth
    "X-Requested-With": "XMLHttpRequest",
  };

  // Use Telegram initData if available
  const initData = getInitData();
  if (initData) {
    headers["x-telegram-init-data"] = initData;
    return headers;
  }

  // Fallback to JWT token
  const tokens = getStoredTokens();
  if (tokens && !isTokenExpired(tokens.expiresAt)) {
    headers["Authorization"] = `Bearer ${tokens.accessToken}`;
  }

  return headers;
}

// =============================================================================
// CLIENT
// =============================================================================

/**
 * Execute a GraphQL query/mutation for authentication purposes.
 * 
 * This is a lightweight client used only for auth operations.
 * For all other operations, use URQL hooks.
 * 
 * @param query - GraphQL query/mutation string
 * @param variables - Variables for the operation
 * @returns The data from the response
 * @throws AuthGraphQLError on failure
 */
export async function authGraphql<T>(
  query: string,
  variables?: Record<string, unknown>
): Promise<T> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

  try {
    const response = await fetch(GRAPHQL_URL, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({ query, variables }),
      signal: controller.signal,
    });

    const json: GraphQLResponse<T> = await response.json();

    if (json.errors?.length) {
      const firstError = json.errors[0];
      authLog.error("Auth GraphQL error:", firstError.message);
      throw new AuthGraphQLError(firstError.message, {
        code: firstError.extensions?.code,
      });
    }

    if (!json.data) {
      throw new AuthGraphQLError("No data in response");
    }

    return json.data;
  } catch (error) {
    clearTimeout(timeout);

    if (error instanceof AuthGraphQLError) {
      throw error;
    }

    // Handle network/timeout errors
    const isNetwork = error instanceof Error && (
      error.name === "AbortError" ||
      error.message.toLowerCase().includes("network") ||
      error.message.toLowerCase().includes("fetch") ||
      error.message.toLowerCase().includes("failed to fetch")
    );

    const message = error instanceof Error 
      ? error.message 
      : "Auth request failed";

    authLog.error("Auth request failed:", message);
    throw new AuthGraphQLError(message, { isNetworkError: isNetwork });
  } finally {
    clearTimeout(timeout);
  }
}

// =============================================================================
// NETWORK DETECTION
// =============================================================================

/**
 * Check if an error is a network error (for offline detection).
 */
export function isAuthNetworkError(error: unknown): boolean {
  if (error instanceof AuthGraphQLError) {
    return error.isNetworkError;
  }
  if (error instanceof Error) {
    const msg = error.message.toLowerCase();
    return (
      msg.includes("network") ||
      msg.includes("fetch") ||
      msg.includes("failed to fetch") ||
      msg.includes("networkerror") ||
      msg.includes("connection") ||
      msg.includes("econnrefused") ||
      msg.includes("timeout") ||
      msg.includes("aborted")
    );
  }
  return false;
}
